import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { OmCard, OmHead, OmKpi, OmTable, Row, Td, Pill, statusTone, GOLD, MUTED, NAVY, RED, GREEN, AMBER, FIELD } from "@/components/om/OmBits";

const today = () => new Date().toISOString().slice(0, 10);
const BLANK = { task_name: "", asset_id: "", maintenance_type: "Inspection", frequency: "Monthly", custom_days: 30, duration_hours: 2, technician: "", checklist: [], tools_spares: "", reference_doc: "", next_due: today() };

export default function OmPreventive() {
  const [schedules, setSchedules] = useState([]);
  const [orders, setOrders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [checkText, setCheckText] = useState("");
  const [wo, setWo] = useState(null);
  const [view, setView] = useState("board");
  const [month, setMonth] = useState(today().slice(0, 7));

  const load = () => {
    api.get("/om/pm-schedules").then(({ data }) => setSchedules(data));
    api.get("/om/work-orders").then(({ data }) => setOrders(data));
  };
  useEffect(() => {
    load();
    api.get("/om/assets").then(({ data }) => setAssets(data));
    api.get("/om/meta").then(({ data }) => setMeta(data));
  }, []);

  const stats = useMemo(() => {
    const inMonth = orders.filter((o) => (o.due_date || "").slice(0, 7) === month);
    const done = inMonth.filter((o) => ["Completed", "Verified"].includes(o.status));
    return { scheduled: inMonth.length, completed: done.length, overdue: orders.filter((o) => o.overdue).length,
      compliance: inMonth.length ? Math.round((done.length / inMonth.length) * 100) : 100 };
  }, [orders, month]);

  const create = async () => {
    try {
      await api.post("/om/pm-schedules", {
        ...form, duration_hours: Number(form.duration_hours), custom_days: Number(form.custom_days),
        checklist: checkText.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("PM schedule created — work orders raise 7 days before due date");
      setOpen(false); setForm(BLANK); setCheckText(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveWo = async (status) => {
    try {
      await api.put(`/om/work-orders/${wo.id}`, {
        status, checklist: wo.checklist, time_spent_hours: Number(wo.time_spent_hours || 0), observations: wo.observations || "",
      });
      toast.success(`Work order ${wo.code} · ${status}`);
      setWo(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const escalate = async () => {
    const { data } = await api.post("/om/pm/escalate-overdue");
    toast[data.overdue ? "error" : "success"](data.overdue ? `${data.overdue} overdue task(s) escalated to the O&M Manager` : "Nothing overdue");
  };

  const days = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const total = new Date(y, m, 0).getDate();
    const pad = (first.getDay() + 6) % 7;
    const cells = Array.from({ length: pad }, () => null);
    for (let d = 1; d <= total; d++) {
      const iso = `${month}-${String(d).padStart(2, "0")}`;
      cells.push({ d, iso, items: orders.filter((o) => o.due_date === iso) });
    }
    return cells;
  }, [month, orders]);

  const colorOf = (o) => o.status === "Verified" || o.status === "Completed" ? GREEN : o.overdue ? RED : GOLD;

  return (
    <div data-testid="om-preventive-page">
      <OmHead title="Preventive Maintenance" subtitle="Schedules, auto-generated work orders, checklists and manager sign-off.">
        <Button variant="outline" onClick={() => setView(view === "board" ? "calendar" : "board")} style={{ borderColor: `${GOLD}66`, color: GOLD, background: "transparent" }} data-testid="om-pm-view-toggle">
          {view === "board" ? "Calendar view" : "List view"}
        </Button>
        <Button variant="outline" onClick={escalate} style={{ borderColor: `${RED}66`, color: RED, background: "transparent" }} data-testid="om-pm-escalate-btn">
          <AlertTriangle className="w-4 h-4 mr-1.5" /> Escalate overdue
        </Button>
        <Button onClick={() => setOpen(true)} className="font-semibold" style={{ background: GOLD, color: NAVY, borderRadius: 8 }} data-testid="om-pm-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New PM schedule
        </Button>
      </OmHead>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <OmKpi label="Scheduled this month" value={stats.scheduled} testid="kpi-pm-scheduled" />
        <OmKpi label="Completed" value={stats.completed} tone={GREEN} />
        <OmKpi label="Overdue" value={stats.overdue} tone={stats.overdue ? RED : GREEN} testid="kpi-pm-overdue" />
        <OmKpi label="Compliance" value={`${stats.compliance}%`} testid="kpi-pm-compliance" />
      </div>

      {view === "calendar" ? (
        <OmCard testid="om-pm-calendar">
          <div className="flex items-center gap-2 mb-3">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-[160px]" style={FIELD} data-testid="om-pm-month-input" />
            <div className="flex gap-2 ml-auto text-[11px]" style={{ color: MUTED }}>
              {[["Upcoming", GOLD], ["Completed", GREEN], ["Overdue", RED]].map(([l, c]) => (
                <span key={l} className="inline-flex items-center gap-1"><span style={{ width: 8, height: 8, background: c, borderRadius: 2 }} /> {l}</span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="text-[10px] uppercase tracking-wider pb-1" style={{ color: MUTED }}>{d}</div>
            ))}
            {days.map((c, i) => (
              <div key={i} className="min-h-[68px] p-1.5 border" style={{ borderColor: "rgba(255,255,255,0.07)", borderRadius: 6 }} data-testid={c ? `om-pm-day-${c.iso}` : undefined}>
                {c ? (
                  <>
                    <div className="text-[10.5px] tabular-nums" style={{ color: MUTED }}>{c.d}</div>
                    {c.items.slice(0, 3).map((o) => (
                      <button key={o.id} onClick={() => setWo({ ...o })} className="block w-full text-left text-[10px] mt-1 px-1 py-0.5 truncate" style={{ background: `${colorOf(o)}22`, color: colorOf(o), borderRadius: 3 }}>
                        {o.task_name}
                      </button>
                    ))}
                    {c.items.length > 3 ? <div className="text-[9.5px] mt-0.5" style={{ color: MUTED }}>+{c.items.length - 3} more</div> : null}
                  </>
                ) : null}
              </div>
            ))}
          </div>
        </OmCard>
      ) : (
        <div className="grid gap-3">
          <OmCard testid="om-wo-card">
            <h3 className="text-white font-semibold text-[15px] mb-2">Work orders</h3>
            <OmTable head={["WO", "Task", "Asset", "Type", "Technician", "Due", "Status", ""]}>
              {orders.map((o, i) => (
                <Row key={o.id} i={i} testid={`om-wo-row-${o.code}`}>
                  <Td className="tabular-nums" style={{ color: MUTED }}>{o.code}</Td>
                  <Td>{o.task_name}</Td>
                  <Td><span style={{ color: MUTED }}>{o.asset_code}</span> {o.asset_name}</Td>
                  <Td style={{ color: MUTED }}>{o.maintenance_type}</Td>
                  <Td style={{ color: MUTED }}>{o.technician || "—"}</Td>
                  <Td className="tabular-nums">{o.due_date}</Td>
                  <Td><Pill tone={o.overdue ? "bad" : statusTone(o.status)}>{o.overdue ? "Overdue" : o.status}</Pill></Td>
                  <Td>
                    <button onClick={() => setWo({ ...o })} className="text-[11.5px] hover:underline" style={{ color: GOLD }} data-testid={`om-wo-open-${o.code}`}>Open</button>
                  </Td>
                </Row>
              ))}
              {orders.length === 0 ? <Row i={0}><Td className="text-[12.5px]">No work orders yet — create a PM schedule.</Td></Row> : null}
            </OmTable>
          </OmCard>

          <OmCard testid="om-pm-schedules-card">
            <h3 className="text-white font-semibold text-[15px] mb-2">PM schedules</h3>
            <OmTable head={["Task", "Asset", "Type", "Frequency", "Duration", "Technician", "Next due", "Steps"]}>
              {schedules.map((s, i) => (
                <Row key={s.id} i={i} testid={`om-pm-row-${s.id}`}>
                  <Td>{s.task_name}</Td>
                  <Td><span style={{ color: MUTED }}>{s.asset_code}</span> {s.asset_name}</Td>
                  <Td style={{ color: MUTED }}>{s.maintenance_type}</Td>
                  <Td><Pill tone="gold">{s.frequency}</Pill></Td>
                  <Td className="tabular-nums">{s.duration_hours} h</Td>
                  <Td style={{ color: MUTED }}>{s.technician || "—"}</Td>
                  <Td className="tabular-nums">{s.next_due}</Td>
                  <Td className="tabular-nums">{(s.checklist || []).length}</Td>
                </Row>
              ))}
              {schedules.length === 0 ? <Row i={0}><Td className="text-[12.5px]">No schedules yet.</Td></Row> : null}
            </OmTable>
          </OmCard>
        </div>
      )}

      {/* new schedule */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: "#112240", borderColor: `${GOLD}33` }}>
          <DialogHeader><DialogTitle className="text-white">New PM schedule</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Task name</Label>
              <Input value={form.task_name} onChange={(e) => setForm({ ...form, task_name: e.target.value })} style={FIELD} data-testid="om-pm-task-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Asset</Label>
              <select value={form.asset_id} onChange={(e) => setForm({ ...form, asset_id: e.target.value })} className="w-full h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid="om-pm-asset-select">
                <option value="">Select asset</option>
                {assets.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
              </select>
            </div>
            {[["maintenance_type", "Maintenance type", meta?.maintenance_types], ["frequency", "Frequency", meta?.frequencies]].map(([k, l, opts]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-[11px] uppercase" style={{ color: MUTED }}>{l}</Label>
                <select value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid={`om-pm-${k}-select`}>
                  {(opts || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
            {[["duration_hours", "Estimated duration (h)", "number"], ["technician", "Assigned technician", "text"],
              ["next_due", "First due date", "date"], ["tools_spares", "Required tools & spares", "text"],
              ["reference_doc", "Reference document", "text"]].map(([k, l, t]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-[11px] uppercase" style={{ color: MUTED }}>{l}</Label>
                <Input type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} style={FIELD} data-testid={`om-pm-${k}-input`} />
              </div>
            ))}
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Checklist — one step per line</Label>
              <Textarea rows={4} value={checkText} onChange={(e) => setCheckText(e.target.value)} style={FIELD} data-testid="om-pm-checklist-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={!form.task_name || !form.asset_id} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-pm-save-btn">Create schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* work order drawer */}
      <Dialog open={!!wo} onOpenChange={(o) => !o && setWo(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" style={{ background: "#112240", borderColor: `${GOLD}33` }} data-testid="om-wo-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{wo?.code} · {wo?.task_name}</DialogTitle>
          </DialogHeader>
          {wo ? (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-[12px]" style={{ color: MUTED }}>
                <Pill tone={wo.overdue ? "bad" : statusTone(wo.status)}>{wo.overdue ? "Overdue" : wo.status}</Pill>
                <span>{wo.asset_code} · {wo.asset_name}</span>
                <span>due {wo.due_date}</span>
              </div>
              <div className="border" style={{ borderColor: `${GOLD}22`, borderRadius: 8 }}>
                <div className="px-3 py-2 text-[10.5px] uppercase tracking-[0.12em]" style={{ color: GOLD, borderBottom: `1px solid ${GOLD}22` }}>Checklist</div>
                {(wo.checklist || []).length === 0 ? <p className="px-3 py-2 text-[12px]" style={{ color: MUTED }}>No checklist steps.</p> : wo.checklist.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2">
                    <span className="text-white text-[12.5px] flex-1">{c.step}</span>
                    {["Pass", "Fail", "NA"].map((v) => (
                      <button
                        key={v}
                        onClick={() => setWo({ ...wo, checklist: wo.checklist.map((x, j) => j === i ? { ...x, result: v } : x) })}
                        className="px-2 py-0.5 text-[10.5px]"
                        style={{ borderRadius: 999, border: `1px solid ${c.result === v ? (v === "Pass" ? GREEN : v === "Fail" ? RED : MUTED) : "rgba(255,255,255,0.15)"}`, color: c.result === v ? (v === "Pass" ? GREEN : v === "Fail" ? RED : MUTED) : MUTED }}
                        data-testid={`om-wo-check-${i}-${v}`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Time spent (h)</Label>
                  <Input type="number" value={wo.time_spent_hours || ""} onChange={(e) => setWo({ ...wo, time_spent_hours: e.target.value })} style={FIELD} data-testid="om-wo-hours-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Technician</Label>
                  <Input value={wo.technician || ""} readOnly style={{ ...FIELD, opacity: 0.7 }} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Observations</Label>
                <Textarea rows={2} value={wo.observations || ""} onChange={(e) => setWo({ ...wo, observations: e.target.value })} style={FIELD} data-testid="om-wo-observations-input" />
              </div>
              {wo.verified_by ? (
                <p className="text-[11.5px]" style={{ color: GREEN }}>
                  <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />
                  Signed off by {wo.verified_by} on {new Date(wo.verified_at).toLocaleString("en-IN")}
                </p>
              ) : null}
            </div>
          ) : null}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => saveWo("In Progress")} style={{ borderColor: `${AMBER}66`, color: AMBER, background: "transparent" }} data-testid="om-wo-start-btn">Start</Button>
            <Button variant="outline" onClick={() => saveWo("Completed")} style={{ borderColor: `${GREEN}66`, color: GREEN, background: "transparent" }} data-testid="om-wo-complete-btn">Mark complete</Button>
            <Button onClick={() => saveWo("Verified")} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-wo-verify-btn">Verify & sign off</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
